import { TIERED_PRODUCTS, TieredPriceProductId } from "@project/common";
import { EChartTimeseries } from "../../../components/EChartTimeseries.js";
import { UsageChartWrapper, type BasicUsageChartProps } from "./UsageChartWrapper.js";
import { useTeamSeatsUsage } from "./hooks/fetchUsages.js";
import { useProductNames } from "./hooks/productLabels.js";
import { useMemoizedBarSeries } from "../../../hooks/chart.js";
import { resolveUsageEventDeltas } from "./utils/usageEventsProcessors.js";
import { IconUsers } from "@tabler/icons-react";
import { checkIsApproachingLimit } from "./utils/limits.js";
import { useTeamSeatsUsageSum } from "./hooks/fetchSums.js";
import { FormattedMessage, useIntl } from "react-intl";
import { Text } from "@mantine/core";

export function TeamSeatsUsageChart({
  from,
  to,
  interval,
  addToDetailedView,
}: BasicUsageChartProps) {
  const {
    teamSeatsUsage,
    isLoadingTeamSeats: isLoadingTeamSeatsHist,
    isErrorTeamSeats: isErrorTeamSeatsHist,
  } = useTeamSeatsUsage({ from, to, interval });

  const {
    usageSum,
    isLoadingTeamSeats: isLoadingTeamSeatsSum,
    isErrorTeamSeats: isErrorTeamSeatsSum,
  } = useTeamSeatsUsageSum({ to });

  const intl = useIntl();
  const getName = useProductNames();
  const title = getName(TieredPriceProductId.TieredAccountUser);
  const resolved = resolveUsageEventDeltas(teamSeatsUsage, usageSum);

  const series = useMemoizedBarSeries(
    resolved,
    title,
    TIERED_PRODUCTS[TieredPriceProductId.TieredAccountUser].freeTierAllowance,
    intl.formatMessage({ defaultMessage: "1 free team seat", id: "allowance.seats" }),
  );

  return (
    <UsageChartWrapper
      title={title}
      iconColor="orange.4"
      icon={<IconUsers size={20} />}
      addToDetailedView={addToDetailedView}
      hasUsage={teamSeatsUsage.length > 0}
      hasError={isErrorTeamSeatsHist || isErrorTeamSeatsSum}
      isLoading={isLoadingTeamSeatsHist || isLoadingTeamSeatsSum}
      isApproachingLimit={checkIsApproachingLimit(teamSeatsUsage, TieredPriceProductId.TieredAccountUser)}
    >
      <EChartTimeseries series={series} />
      <Text mt="sm">
        <FormattedMessage
          defaultMessage="Active team seats: {usageSum}"
          values={{ usageSum }}
          id="teamSeats.sum"
        />
      </Text>
    </UsageChartWrapper>
  );
};
